import type {
  DiaryMemoryFactKind,
  DiaryMemoryDraft,
  DiaryMemorySource,
  DiaryMemorySuggestion,
  DiaryMemoryType,
} from '@/types/diary';

const BODY_PREVIEW_LIMIT = 72;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const NAME_FACT_PATTERNS = [
  /\bmy name is\s+[^\n.!?]+/i,
  /\bi(?:'m| am)\s+called\s+[^\n.!?]+/i,
  /\bmein name ist\s+[^\n.!?]+/i,
  /\bich hei(?:ß|ss)e\s+[^\n.!?]+/i,
];

type ChatTurnMessage = {
  id?: string | null;
  role: 'user' | 'assistant' | 'system';
  content: string;
};

export interface DiaryMemoryTurn {
  userMessage: {
    id: string;
    content: string;
  };
  assistantMessage: {
    id: string;
    content: string;
  };
}

const DIARY_TYPE_PATTERNS: Array<{ type: DiaryMemoryType; pattern: RegExp }> = [
  {
    type: 'constraint',
    pattern:
      /\b(avoid|can't|cannot|unable to|limit|restriction|flare|sensitive|trigger|no\s+impact|no\s+gluten|skip)\b/i,
  },
  {
    type: 'medical',
    pattern:
      /\b(pain|injury|injured|doctor|physio|physiotherapy|therapy|allergy|medication|blood pressure|migraine|knee|back|hip|ankle|shoulder)\b/i,
  },
  {
    type: 'goal',
    pattern:
      /\b(goal|target|want to|aim to|training for|build muscle|lose weight|sleep better|improve|focus on)\b/i,
  },
  {
    type: 'preference',
    pattern: /\b(prefer|like|love|dislike|hate|favorite|favourite|works best|rather)\b/i,
  },
  {
    type: 'nutrition',
    pattern:
      /\b(protein|meal|breakfast|lunch|dinner|diet|snack|calories|macro|fiber|fibre|carbs)\b/i,
  },
  {
    type: 'training',
    pattern:
      /\b(workout|training|lift|lifting|gym|run|running|squat|bench|deadlift|cardio|recovery day)\b/i,
  },
];

function truncate(value: string, maxLength: number) {
  if (value.length <= maxLength) return value;
  return `${value.slice(0, Math.max(0, maxLength - 1)).trimEnd()}…`;
}

function attachDiaryFactKindMetadata(
  metadata: Record<string, unknown>,
  factKind: DiaryMemoryFactKind | null,
) {
  if (factKind) {
    metadata.fact_kind = factKind;
  } else {
    delete metadata.fact_kind;
  }

  return metadata;
}

export function inferDiaryMemoryType(body: string): DiaryMemoryType {
  const normalized = body.trim();
  if (!normalized) return 'life_context';

  const match = DIARY_TYPE_PATTERNS.find(({ pattern }) => pattern.test(normalized));
  return match?.type ?? 'life_context';
}

export function inferDiaryMemoryFactKind(body: string): DiaryMemoryFactKind | null {
  const normalized = body.replace(/\s+/g, ' ').trim();
  if (!normalized) return null;

  return NAME_FACT_PATTERNS.some((pattern) => pattern.test(normalized)) ? 'name' : null;
}

export function createDiaryMemoryTitle(body: string, fallback = 'New memory') {
  const normalized = body.replace(/\s+/g, ' ').trim();
  if (!normalized) return fallback;

  const firstSentence = normalized
    .split(/[.!?\n]/)
    .find((part) => part.trim().length > 0)
    ?.trim();
  return truncate(firstSentence || normalized, 56);
}

export function formatDiaryPreview(body: string, maxLength = BODY_PREVIEW_LIMIT) {
  return truncate(body.replace(/\s+/g, ' ').trim(), maxLength);
}

export function getDiaryMemoryTypeLabel(type: DiaryMemoryType) {
  switch (type) {
    case 'medical':
      return 'Medical';
    case 'goal':
      return 'Goal';
    case 'preference':
      return 'Preference';
    case 'constraint':
      return 'Constraint';
    case 'nutrition':
      return 'Nutrition';
    case 'training':
      return 'Training';
    case 'life_context':
    default:
      return 'Life context';
  }
}

export function getDiarySourceLabel(source: DiaryMemorySource) {
  switch (source) {
    case 'chat_explicit':
      return 'Saved from chat';
    case 'chat_suggested':
      return 'Chat suggestion';
    case 'vault_import':
      return 'Vault import';
    case 'manual':
    default:
      return 'Manual';
  }
}

export function isPersistedChatMessageId(id: string | null | undefined): id is string {
  return typeof id === 'string' && UUID_PATTERN.test(id.trim());
}

export function resolveDiaryMemoryTurnFromAssistantMessage(
  messages: ChatTurnMessage[],
  assistantMessageId: string,
): DiaryMemoryTurn | null {
  if (!isPersistedChatMessageId(assistantMessageId)) return null;

  const assistantIndex = messages.findIndex(
    (message) =>
      message.role === 'assistant' &&
      message.id === assistantMessageId &&
      message.content.trim().length > 0,
  );
  if (assistantIndex < 0) return null;

  for (let index = assistantIndex - 1; index >= 0; index -= 1) {
    const candidate = messages[index];
    if (!candidate || candidate.role !== 'user') continue;

    const userContent = candidate.content.trim();
    if (!userContent || !isPersistedChatMessageId(candidate.id)) continue;

    const assistantMessage = messages[assistantIndex];
    const assistantContent = assistantMessage?.content.trim() ?? '';
    if (!assistantContent) return null;

    return {
      userMessage: {
        id: candidate.id,
        content: userContent,
      },
      assistantMessage: {
        id: assistantMessageId,
        content: assistantContent,
      },
    };
  }

  return null;
}

export function buildDiaryMemoryDraftFromSuggestion(params: {
  suggestion: DiaryMemorySuggestion;
  turn: DiaryMemoryTurn;
  sessionId?: string | null;
}): DiaryMemoryDraft {
  const body = params.suggestion.body?.trim() ?? '';
  const title = params.suggestion.title?.trim() || createDiaryMemoryTitle(body);
  const factKind = params.suggestion.fact_kind ?? inferDiaryMemoryFactKind(body);

  return {
    title,
    body,
    memory_type: params.suggestion.memory_type ?? inferDiaryMemoryType(body),
    source: 'chat_suggested',
    confidence: params.suggestion.confidence ?? 0.85,
    user_confirmed: true,
    is_pinned: false,
    status: 'active',
    metadata: {
      ...attachDiaryFactKindMetadata(
        {
          captured_via: 'remember_turn_suggestion',
          capture_scope: 'turn',
          origin_assistant_message_id: params.turn.assistantMessage.id,
          suggestion_confidence: params.suggestion.confidence ?? 0.85,
        },
        factKind,
      ),
    },
    origin_chat_session_id: params.sessionId ?? null,
    origin_chat_message_id: params.turn.userMessage.id,
  };
}

export function buildDiaryMemoryDraftFromAssistantResponse(params: {
  content: string;
  sessionId?: string | null;
  userMessageId?: string | null;
  assistantMessageId?: string | null;
}): DiaryMemoryDraft {
  const body = params.content.trim();
  const factKind = inferDiaryMemoryFactKind(body);

  return {
    title: createDiaryMemoryTitle(body),
    body,
    memory_type: inferDiaryMemoryType(body),
    source: 'chat_suggested',
    confidence: 1,
    user_confirmed: true,
    is_pinned: false,
    status: 'active',
    metadata: {
      ...attachDiaryFactKindMetadata(
        {
          captured_via: 'remember_answer_text',
          capture_scope: 'assistant',
          origin_assistant_message_id: params.assistantMessageId ?? null,
        },
        factKind,
      ),
    },
    origin_chat_session_id: params.sessionId ?? null,
    origin_chat_message_id: params.userMessageId ?? null,
  };
}

export function buildDiaryMemoryDraftFromMessage(params: {
  content: string;
  sessionId?: string | null;
  messageId?: string | null;
}): DiaryMemoryDraft {
  const body = params.content.trim();
  const factKind = inferDiaryMemoryFactKind(body);

  return {
    title: createDiaryMemoryTitle(body),
    body,
    memory_type: inferDiaryMemoryType(body),
    source: 'chat_explicit',
    confidence: 1,
    user_confirmed: true,
    is_pinned: false,
    status: 'active',
    metadata: {
      ...attachDiaryFactKindMetadata(
        {
          captured_via: 'remember_action',
        },
        factKind,
      ),
    },
    origin_chat_session_id: params.sessionId ?? null,
    origin_chat_message_id: params.messageId ?? null,
  };
}
